import React, { useCallback, useState } from "react";
import { AHInput, Box, AHButton } from "arkh";
import {
  ActionCard,
  ActionCardDescription,
  ActionCardDescriptionContainer,
  ActionCardTitle,
} from "../../OS/Login/ActionCardTitle";
import { CompositeScreenProps } from "@react-navigation/native";
import { FormattedMessage, useIntl } from "react-intl";
import { useKeyboard } from "../../OS/useKeyboard";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { OnboardingNavigatorParamList } from "./navigationParams";
import { UserLoggedInNavigatorParamList } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useUser } from "../../OS/Login/State";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { Platform } from "react-native";
import { graphql, useMutation } from "react-relay";

import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { SetNameScreenMutation } from "./__generated__/SetNameScreenMutation.graphql";

type SetNameScreenProps = CompositeScreenProps<
  NativeStackScreenProps<OnboardingNavigatorParamList, "OnboardingSetName">,
  NativeStackScreenProps<UserLoggedInNavigatorParamList, "Onboarding">
>;

export function SetNameScreen(props: SetNameScreenProps) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerSetNameScreen {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerSetNameScreen(props: SetNameScreenProps & { fetchKey: string }) {
  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const insets = useSafeAreaInsets();
  const user = useUser().user;
  const [name, setName] = useState("");

  const [updateName, loading] = useMutation<SetNameScreenMutation>(graphql`
    mutation SetNameScreenMutation($name: String!) {
      me {
        setName(name: $name) {
          id
          name
        }
      }
    }
  `);

  const onUpdateName = useCallback(() => {
    updateName({
      variables: {
        name,
      },
      onCompleted: () => {
        props.navigation.navigate("OnboardingFinishedSetup");
      },
      onError: (error) => {
        console.log("SetName error", error);
      },
      optimisticResponse: {
        me: {
          __typename: "MeMutation",
          setName: {
            __typename: "User",
            id: user.sub,
            name,
          },
        },
      },
    });
  }, [name, props.navigation, updateName, user.sub]);

  const keyboard = useKeyboard();

  return (
    <Box
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingBottom:
          Platform.OS === "ios" ? keyboard.availableRect.bottom : insets.bottom,
      }}
      spaceHorizontal="medium"
      color="background"
    >
      <ActionCard>
        <ActionCardTitle>
          <FormattedMessage
            defaultMessage="Welcome to StoreSprint"
            description="Onboarding set name screen title"
          />
        </ActionCardTitle>
        <ActionCardDescriptionContainer>
          <ActionCardDescription>
            <FormattedMessage
              defaultMessage="Before we let you jump right in and start working, we need to
          do some quick set up of your account. Start by entering your
          name:"
              description="Onboarding set name screen description"
            />
          </ActionCardDescription>
        </ActionCardDescriptionContainer>
        <Box style={{ flexDirection: "row" }} spaceVertical="small">
          <AHInput
            color="background"
            cornerRadius="large"
            space="small"
            textType="title"
            style={{
              flex: 1,
            }}
            autoFocus
            autoCapitalize="words"
            autoComplete="name"
            autoCorrect
            importantForAutofill="yes"
            placeholder={intl.formatMessage({
              defaultMessage: "Your name",
              description: "Onboarding set name input placeholder",
            })}
            value={name}
            onChangeText={setName}
            returnKeyType="next"
            onSubmitEditing={() => {
              if (name.length > 0) {
                onUpdateName();
              }
            }}
          />
          <Box spaceRight="medium" />
          <AHButton
            title={intl_tokens.Next}
            themeType="primary"
            loading={loading}
            disabled={name.length === 0}
            onPress={onUpdateName}
          />
        </Box>
      </ActionCard>
    </Box>
  );
}
